import React, { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Drawer } from '@mantine/core';
import { IconMenu2 } from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SHELL_NS } from 'recruiter-i18n/namespaces/shell';
import SideNavBar from 'components/side-nav-bar/SideNavBar';
import {
  isBullhornEmbedRoute,
  isBullhornEmbedSession,
  isInIframe,
} from 'utils/bullhornEmbed';

import classes from './BullhornEmbedNav.module.css';

const ROUTES_WITHOUT_EMBED_NAV = [
  '/login',
  '/authenticate',
  '/web-interview',
  '/web-call',
  '/feedback',
  '/reschedule',
  '/restricted-access',
  '/email-decision',
  '/oauth/consent',
] as const;

export default function BullhornEmbedNav() {
  const { t } = useTranslation(SHELL_NS);
  const location = useLocation();
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    setOpened(false);
  }, [location.pathname]);

  const shouldRender = useMemo(() => {
    if (
      !isBullhornEmbedSession() &&
      !(isBullhornEmbedRoute(location.pathname) && isInIframe())
    ) {
      return false;
    }

    if (
      location.pathname.includes('/approve-reissue') ||
      location.pathname.includes('/reject-reissue')
    ) {
      return false;
    }

    return !ROUTES_WITHOUT_EMBED_NAV.some((route) =>
      location.pathname.startsWith(route)
    );
  }, [location.pathname]);

  if (!shouldRender) {
    return null;
  }

  const navAriaLabel = t(opened ? 'nav.closeNavigation' : 'nav.openNavigation');

  return (
    <>
      <ActionIcon
        className={classes.menuButton}
        variant='filled'
        color='blue'
        size='lg'
        aria-label={navAriaLabel}
        aria-expanded={opened}
        onClick={() => setOpened((isOpen) => !isOpen)}
      >
        <IconMenu2 size={20} />
      </ActionIcon>

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton={false}
        padding={0}
        size={280}
        classNames={{ body: classes.drawerBody }}
      >
        <div className={classes.drawerContent}>
          <SideNavBar variant='drawer' onNavigate={() => setOpened(false)} />
        </div>
      </Drawer>
    </>
  );
}
